import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const QuizOverlay = ({ isVisible, onCorrectAnswer, onWrongAnswer, onClose, quizCode, onQuizTitleChange }) => {
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [correctAnswersNeeded] = useState(3);
  const [correctCount, setCorrectCount] = useState(0);
  const [userContext, setUserContext] = useState(null);
  const [wrongQuestions, setWrongQuestions] = useState(new Set());
  const [correctQuestions, setCorrectQuestions] = useState(new Set());
  const [quizContext, setQuizContext] = useState(null);
  const [correctAnswerInfo, setCorrectAnswerInfo] = useState(null);
  // New state for front-loaded questions
  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [quizInfo, setQuizInfo] = useState(null);
  // Quiz completion tracking
  const [quizCompletionId, setQuizCompletionId] = useState(null);
  const [answerResults, setAnswerResults] = useState([]);
  // Game session tracking (for teacher dashboard)
  const [gameSessionId, setGameSessionId] = useState(null);

  // Keep correctCount in sync with correctQuestions size
  useEffect(() => {
    setCorrectCount(correctQuestions.size);
  }, [correctQuestions]);

  // Load all questions when overlay becomes visible (for quiz mode) or fetch single question (for game mode)
  useEffect(() => {
    if (isVisible && !questionsLoaded) {
      if (quizCode) {
        loadAllQuestions();
      } else {
        fetchQuestion();
      }
    }
  }, [isVisible]);

  // Set current question when questions are loaded or index changes
  useEffect(() => {
    if (allQuestions.length > 0 && currentQuestionIndex < allQuestions.length) {
      setQuestion(allQuestions[currentQuestionIndex]);
    } else if (allQuestions.length > 0) {
      // All questions completed in quiz mode
      handleQuizComplete();
    }
  }, [allQuestions, currentQuestionIndex]);

  // Load all questions for quiz mode
  const loadAllQuestions = async () => {
    setLoading(true);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectAnswerInfo(null);

    try {
      const response = await fetch(`/api/quiz/all_questions?quiz_code=${encodeURIComponent(quizCode)}`);
      const data = await response.json();

      if (data.success && data.questions.length > 0) {
        setAllQuestions(data.questions);
        setQuizInfo(data.quiz);
        setQuestionsLoaded(true);
        setCurrentQuestionIndex(0);
        setQuizContext({ quiz_mode: true });
        
        // Create quiz completion record for result tracking
        if (data.quiz?.id) {
          await createQuizCompletion(data.quiz.id);
          // Also create game session for teacher dashboard
          await createGameSession(quizCode);
        }
        
        // Pass quiz title back to parent component
        if (data.quiz && data.quiz.title && onQuizTitleChange) {
          onQuizTitleChange(data.quiz.title);
        }
      } else {
        console.error('Failed to load quiz questions:', data.error);
        // Fallback to single question mode
        setQuestionsLoaded(true);
        fetchQuestion();
      }
    } catch (error) {
      console.error('Error loading quiz questions:', error);
      // Fallback to single question mode  
      setQuestionsLoaded(true);
      fetchQuestion();
    } finally {
      setLoading(false);
    }
  };

  // Fetch single question for game mode
  const fetchQuestion = async () => {
    setLoading(true);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectAnswerInfo(null);

    try {
      let url = '/api/quiz/question';
      const params = new URLSearchParams();
      
      if (quizCode) {
        params.append('quiz_code', quizCode);
      }
      
      // Prioritize wrong questions for retry in quiz mode
      if (quizCode && wrongQuestions.size > 0) {
        params.append('prioritize_questions', Array.from(wrongQuestions).join(','));
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setQuestion(data.question);
        setUserContext(data.user_context);
        setQuizContext(data.quiz_context || { quiz_mode: false });
        
        // Pass quiz title back to parent component
        if (data.question && data.question.quiz_title && onQuizTitleChange) {
          onQuizTitleChange(data.question.quiz_title);
        }
      } else {
        setUserContext(data.user_context);
        setQuizContext(data.quiz_context || { quiz_mode: false });
        console.error('Failed to fetch question:', data.error);
        // Fallback question
        const fallbackQuestion = {
          id: 'fallback',
          text: 'What is 2 + 2?',
          choices: [
            { text: '3', index: 0 },
            { text: '4', index: 1 },
            { text: '5', index: 2 },
            { text: '6', index: 3 }
          ],
          quiz_title: 'Math Practice',
          subject: 'math'
        };
        setQuestion(fallbackQuestion);
        
        // Pass quiz title back to parent component
        if (onQuizTitleChange) {
          onQuizTitleChange(fallbackQuestion.quiz_title);
        }
      }
    } catch (error) {
      console.error('Error fetching question:', error);
      // Use fallback question
      const fallbackQuestion = {
        id: 'fallback',
        text: 'What is 2 + 2?',
        choices: [
          { text: '3', index: 0 },
          { text: '4', index: 1 },
          { text: '5', index: 2 },
          { text: '6', index: 3 }
        ],
        quiz_title: 'Math Practice',
        subject: 'math'
      };
      setQuestion(fallbackQuestion);
      
      // Pass quiz title back to parent component
      if (onQuizTitleChange) {
        onQuizTitleChange(fallbackQuestion.quiz_title);
      }
    } finally {
      setLoading(false);
      setQuestionsLoaded(true);
    }
  };

  const handleAnswerClick = async (answerIndex) => {
    if (selectedAnswer !== null || loading) return;

    setSelectedAnswer(answerIndex);
    setLoading(true);

    try {
      let correct = false;

      if (question.id === 'fallback') {
        // Handle fallback question locally
        correct = answerIndex === 1; // Answer is '4'
      } else if (allQuestions.length > 0) {
        // Handle pre-loaded questions locally
        correct = answerIndex === question.correct_index;
        if (!correct) {
          // Store correct answer info for display
          setCorrectAnswerInfo({
            correctIndex: question.correct_index,
            correctChoice: question.choices[question.correct_index],
            explanation: question.explanation || 'Good try! Keep learning!'
          });
        }
      } else {
        // Submit answer to server for single questions
        const response = await fetch('/api/quiz/answer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({
            question_id: question.id,
            selected_index: answerIndex
          })
        });

        const data = await response.json();
        if (data.success) {
          correct = data.correct;
          if (!correct) {
            // Store correct answer info for display
            setCorrectAnswerInfo({
              correctIndex: data.correct_index,
              correctChoice: data.correct_choice,
              explanation: data.explanation
            });
          }
        }
      }

      setIsCorrect(correct);
      setShowResult(true);
      
      // Record this answer for result tracking
      recordAnswer(question.id, answerIndex, correct);
      
      // Update game session progress
      if (gameSessionId) {
        await updateGameSessionProgress(correct);
      }

      if (correct) {
        // Mark question as correct
        setCorrectQuestions(prev => new Set([...prev, question.id]));
        // Remove from wrong questions if it was there
        setWrongQuestions(prev => {
          const updated = new Set(prev);
          updated.delete(question.id);
          return updated;
        });

        // Calculate new correct count immediately for completion check
        const newCorrectCount = correctQuestions.has(question.id) ? correctQuestions.size : correctQuestions.size + 1;

        // Check if we're in quiz mode with pre-loaded questions
        if (allQuestions.length > 0) {
          // Quiz mode logic - need 3 correct for success
          if (newCorrectCount >= 3) {
            setTimeout(() => {
              handleQuizComplete();
            }, 2000);
          }
        } else if (quizContext?.quiz_mode) {
          // Original quiz mode logic for backwards compatibility
          if (newCorrectCount >= 3 && wrongQuestions.size === 0) {
            setTimeout(() => {
              onCorrectAnswer();
              resetQuiz();
            }, 2000);
          }
        } else {
          // Regular game mode - original logic
          if (newCorrectCount >= correctAnswersNeeded) {
            setTimeout(() => {
              onCorrectAnswer();
              resetQuiz();
            }, 2000);
          }
        }
      } else {
        // Wrong answer - add to wrong questions set
        setWrongQuestions(prev => new Set([...prev, question.id]));
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetQuiz = () => {
    setQuestion(null);
    setSelectedAnswer(null);
    setShowResult(false);
    setIsCorrect(false);
    setCorrectCount(0);
    setWrongQuestions(new Set());
    setCorrectQuestions(new Set());
    setQuizContext(null);
    setCorrectAnswerInfo(null);
    setAllQuestions([]);
    setCurrentQuestionIndex(0);
    setQuestionsLoaded(false);
    setQuizInfo(null);
    setQuizCompletionId(null);
    setAnswerResults([]);
    setGameSessionId(null);
  };

  const handleNextQuestion = () => {
    if (allQuestions.length > 0) {
      // Quiz mode - advance to next question or cycle through wrong answers
      const unansweredQuestions = [];
      const wrongAnsweredQuestions = [];
      
      for (let i = 0; i < allQuestions.length; i++) {
        const q = allQuestions[i];
        if (!correctQuestions.has(q.id) && !wrongQuestions.has(q.id)) {
          unansweredQuestions.push(i);
        } else if (wrongQuestions.has(q.id)) {
          wrongAnsweredQuestions.push(i);
        }
      }
      
      // Priority: unanswered questions first, then wrong questions
      let nextIndex;
      if (unansweredQuestions.length > 0) {
        nextIndex = unansweredQuestions[0];
      } else if (wrongAnsweredQuestions.length > 0) {
        nextIndex = wrongAnsweredQuestions[0];
      } else {
        // All questions answered correctly - complete quiz
        handleQuizComplete();
        return;
      }
      
      setCurrentQuestionIndex(nextIndex);
      setSelectedAnswer(null);
      setShowResult(false);
      setCorrectAnswerInfo(null);
    } else {
      // Game mode - fetch new question
      fetchQuestion();
    }
  };

  const handleQuizComplete = async () => {
    // Submit final quiz results if we have a completion record
    if (quizCompletionId && answerResults.length > 0) {
      await submitQuizResults();
    }
    
    // Complete the game session for teacher dashboard
    if (gameSessionId) {
      await completeGameSession();
    }
    
    onCorrectAnswer();
    resetQuiz();
  };

  // Create a quiz completion record
  const createQuizCompletion = async (quizId) => {
    if (!quizId) return;
    
    try {
      const response = await fetch('/api/quiz_completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          quiz_id: quizId,
          feature_type: 'jumping_game'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQuizCompletionId(data.completion_id);
        }
      }
    } catch (error) {
      console.error('Error creating quiz completion:', error);
    }
  };

  // Submit all quiz answers and calculate final score
  const submitQuizResults = async () => {
    try {
      const response = await fetch(`/api/quiz_completions/${quizCompletionId}/submit_results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          answers: answerResults
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Quiz results submitted:', data);
      }
    } catch (error) {
      console.error('Error submitting quiz results:', error);
    }
  };

  // Track an individual answer
  const recordAnswer = (questionId, selectedIndex, isCorrect) => {
    const answerRecord = {
      question_id: questionId,
      selected_choice_index: selectedIndex,
      correct: isCorrect,
      answered_at: new Date().toISOString()
    };
    
    setAnswerResults(prev => {
      // Remove any existing answer for this question and add the new one
      const filtered = prev.filter(answer => answer.question_id !== questionId);
      return [...filtered, answerRecord];
    });
  };

  // Create a game session for teacher dashboard tracking
  const createGameSession = async (quizCode) => {
    if (!quizCode) return;
    
    try {
      const response = await fetch('/api/quiz_game_sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          quiz_code: quizCode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGameSessionId(data.session.id);
        }
      }
    } catch (error) {
      console.error('Error creating game session:', error);
    }
  };

  // Update game session progress
  const updateGameSessionProgress = async (isCorrect) => {
    if (!gameSessionId) return;
    
    try {
      // Calculate current totals
      const totalAnswered = answerResults.length + 1; // +1 for current answer
      const totalCorrect = answerResults.filter(a => a.correct).length + (isCorrect ? 1 : 0);
      
      const response = await fetch(`/api/quiz_game_sessions/${gameSessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          questions_answered: totalAnswered,
          questions_correct: totalCorrect,
          levels_climbed: 1 // For quiz mode, we consider it as 1 level
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Game session progress updated:', data);
      }
    } catch (error) {
      console.error('Error updating game session:', error);
    }
  };

  // Complete the game session
  const completeGameSession = async () => {
    if (!gameSessionId) return;
    
    try {
      const response = await fetch(`/api/quiz_game_sessions/${gameSessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Game session completed:', data);
      }
    } catch (error) {
      console.error('Error completing game session:', error);
    }
  };

  const getButtonColor = (index) => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-green-500', 'bg-blue-500'];
    return colors[index % colors.length];
  };

  const getButtonHoverColor = (index) => {
    const colors = ['hover:bg-red-600', 'hover:bg-orange-600', 'hover:bg-green-600', 'hover:bg-blue-600'];
    return colors[index % colors.length];
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">🧠 Learning Challenge!</h2>
              <p className="text-purple-100 mt-1">
                {allQuestions.length > 0 
                  ? `Quiz: ${allQuestions.length} questions • Get 3 correct to continue playing!`
                  : `Answer ${correctAnswersNeeded} questions correctly to continue playing!`
                }
              </p>
              {userContext && !userContext.logged_in && (
                <p className="text-purple-200 text-sm mt-1">
                  💡 Sign up to get questions for your grade level!
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm opacity-90">Progress</div>
              <div className="text-lg font-bold">{correctCount}/{correctAnswersNeeded}</div>
              {userContext && userContext.logged_in && userContext.user_grade && (
                <div className="text-xs opacity-75 mt-1">Grade {userContext.user_grade}</div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && !question ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">
                {quizCode ? 'Loading quiz questions...' : 'Loading your question...'}
              </p>
              {quizCode && (
                <p className="text-gray-500 text-sm mt-2">
                  Preparing all questions for the best learning experience!
                </p>
              )}
            </div>
          ) : question ? (
            <>
              {/* Sign-up encouragement for non-logged users */}
              {userContext && !userContext.logged_in && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🎯</span>
                    <h3 className="text-lg font-bold text-gray-800">Get Questions Perfect for You!</h3>
                  </div>
                  <p className="text-gray-700 text-sm mb-3">
                    Create an account and set your grade level to get questions that match your learning level.
                    Right now you're getting random questions that might be too easy or too hard.
                  </p>
                  <div className="flex gap-2">
                    <a
                      href="/users/sign_up"
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Sign Up Free
                    </a>
                    <a
                      href="/users/sign_in"
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Sign In
                    </a>
                  </div>
                </div>
              )}

              {/* Question */}
              <div className="mb-6">
                <div className="text-sm text-gray-500 mb-2">
                  📚 {question.quiz_title} • {question.subject}
                  {userContext && userContext.question_grade && (
                    <span className="ml-2 bg-gray-100 px-2 py-1 rounded text-xs">
                      Grade {userContext.question_grade}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-800 leading-relaxed">
                  {question.text}
                </h3>
              </div>

              {/* Answer Choices */}
              <div className={`grid gap-3 mb-6 ${
                question.choices.length <= 2 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
              }`}>
                {question.choices.filter(choice => choice && choice.text && choice.text.trim()).map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerClick(index)}
                    disabled={selectedAnswer !== null || loading}
                    className={`${getButtonColor(index)} ${getButtonHoverColor(index)} text-white p-4 rounded-lg text-left font-medium transition-all transform hover:scale-105 disabled:hover:scale-100 ${
                      selectedAnswer === index
                        ? showResult
                          ? isCorrect
                            ? 'ring-4 ring-green-300 bg-green-600'
                            : 'ring-4 ring-red-300 bg-red-600'
                          : 'ring-4 ring-purple-300'
                        : ''
                    } ${
                      selectedAnswer !== null && selectedAnswer !== index ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{choice.text}</span>
                      {selectedAnswer === index && showResult && (
                        <span className="text-white">
                          {isCorrect ? (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Result Message */}
              {showResult && (
                <div className={`p-4 rounded-lg mb-4 ${
                  isCorrect
                    ? 'bg-green-100 border border-green-200'
                    : 'bg-red-100 border border-red-200'
                }`}>
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full mr-3 ${
                      isCorrect ? 'bg-green-200 text-green-600' : 'bg-red-200 text-red-600'
                    }`}>
                      {isCorrect ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h4 className={`font-bold ${
                        isCorrect ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {isCorrect ? '🎉 Correct!' : '💪 Keep trying!'}
                      </h4>
                      <p className={`text-sm ${
                        isCorrect ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {isCorrect
                          ? correctCount >= correctAnswersNeeded
                            ? "Amazing! You can continue playing!"
                            : `Great job! ${Math.max(0, correctAnswersNeeded - correctCount)} more to go!`
                          : "No problem! Try the next question."}
                      </p>
                      
                      {/* Show correct answer when wrong */}
                      {!isCorrect && correctAnswerInfo && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 mb-1">
                            ✓ Correct answer:
                          </p>
                          <p className="text-sm text-blue-700">
                            {correctAnswerInfo.correctChoice?.text || correctAnswerInfo.correctChoice}
                          </p>
                          {correctAnswerInfo.explanation && (
                            <p className="text-xs text-blue-600 mt-2 italic">
                              {correctAnswerInfo.explanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Next Question Button */}
              {showResult && (
                <div className="text-center mb-4">
                  <button
                    onClick={handleNextQuestion}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    {loading ? 'Loading...' : 'Next Question →'}
                  </button>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Correct Answers</span>
                  <span>{correctCount}/{correctAnswersNeeded}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(correctCount / correctAnswersNeeded) * 100}%` }}
                  ></div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Unable to load question. Please try again.</p>
              <button
                onClick={fetchQuestion}
                className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

QuizOverlay.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onCorrectAnswer: PropTypes.func.isRequired,
  onWrongAnswer: PropTypes.func,
  onClose: PropTypes.func,
  quizCode: PropTypes.string,
  onQuizTitleChange: PropTypes.func
};

export default QuizOverlay;